import axios from "axios";
import { getEnvironment } from "../utils/getEnvironment";

const environment = getEnvironment();

export interface LoyaltyConfig {
    loyaltyReferral: boolean;
    loyaltyReferralDiscount: number;
    loyaltyReferralReward: number;
    loyaltyBalanceExpiryDays: number;
    loyaltySubscription: boolean;
    loyaltySubPrice: number;
    loyaltySubDiscount: number | null;
    loyaltySubCashback: number | null;
    loyaltySubNeverExpire: boolean | null;
    loyaltySubShortNotice: number | null;
    loyaltySubShortCancel: number | null;
    loyaltySubWaitlistPriority: boolean | null;
    loyaltyMaxBalancePct: number;
    loyaltyAppDiscount: boolean;
    loyaltyAppDiscountPct: number;
}

export interface BalanceItem {
    userKey: string;
    name: string;
    email: string;
    balance: number;
    updated: string;
}

export interface BalanceTransaction {
    id: number;
    type: string;
    amount: number;
    note: string | null;
    expired: boolean;
    expiresAt: string | null;
    created: string;
}

export interface SubscriberItem {
    userKey: string;
    name: string;
    email: string;
    status: string;
    startedAt: string;
    currentPeriodEnd: string;
    price: number;
}

export interface LoyaltyStats {
    referralCount: number;
    activeSubscribers: number;
    outstandingBalance: number;
    totalRevenue: number;
}

interface PaginatedResponse<T> {
    total: number;
    items: T[];
}

const headers = (uuid: string) => ({ "User-Token": uuid });

export default class Loyalty {
    public static readonly getConfig = async (
        uuid: string,
        brandKey: string
    ): Promise<LoyaltyConfig | undefined> =>
        axios
            .get(`${environment.api_url}/${brandKey}/loyalty`, {
                headers: headers(uuid),
            })
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error);

    public static readonly updateConfig = async (
        uuid: string,
        brandKey: string,
        data: Partial<LoyaltyConfig>
    ) =>
        axios.put(`${environment.api_url}/${brandKey}/loyalty`, data, {
            headers: headers(uuid),
        });

    public static readonly getBalances = async (
        uuid: string,
        brandKey: string,
        start = 0,
        count = 20
    ): Promise<PaginatedResponse<BalanceItem> | undefined> =>
        axios
            .get(`${environment.api_url}/${brandKey}/loyalty/balances`, {
                headers: headers(uuid),
                params: { start, count },
            })
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error);

    public static readonly getUserBalance = async (
        uuid: string,
        brandKey: string,
        userKey: string
    ): Promise<{ balance: number; updated: string | null } | undefined> =>
        axios
            .get(`${environment.api_url}/${brandKey}/loyalty/balances/${userKey}`, {
                headers: headers(uuid),
            })
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error);

    public static readonly getUserTransactions = async (
        uuid: string,
        brandKey: string,
        userKey: string,
        start = 0,
        count = 20
    ): Promise<PaginatedResponse<BalanceTransaction> | undefined> =>
        axios
            .get(
                `${environment.api_url}/${brandKey}/loyalty/balances/${userKey}/transactions`,
                {
                    headers: headers(uuid),
                    params: { start, count },
                }
            )
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error);

    public static readonly adjustBalance = async (
        uuid: string,
        brandKey: string,
        userKey: string,
        amount: number,
        note: string
    ) =>
        axios.post(
            `${environment.api_url}/${brandKey}/loyalty/balances/${userKey}/adjust`,
            { amount, note },
            { headers: headers(uuid) }
        );

    public static readonly getSubscribers = async (
        uuid: string,
        brandKey: string,
        start = 0,
        count = 20
    ): Promise<PaginatedResponse<SubscriberItem> | undefined> =>
        axios
            .get(`${environment.api_url}/${brandKey}/loyalty/subscribers`, {
                headers: headers(uuid),
                params: { start, count },
            })
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error);

    public static readonly getStats = async (
        uuid: string,
        brandKey: string
    ): Promise<LoyaltyStats | undefined> =>
        axios
            .get(`${environment.api_url}/${brandKey}/loyalty/stats`, {
                headers: headers(uuid),
            })
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error);
}
